import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCartItems } from '../../../../redux/slices/cartSlice';
import { CheckOutBox } from '../cart/CheckOutBox';
import WebButton from '../../../../component/library/webButton';
import { CartApiService } from '../../../service/cartApiService';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';
import Logo from '../../../assets/images/common/logo.png';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { useLoader } from '../../../../base/context/LoaderProvider';
import { FiMapPin } from 'react-icons/fi';
import { MdCheckCircle } from 'react-icons/md';
import { FaCoins } from 'react-icons/fa';
import { jwtDecode } from 'jwt-decode';
import { StorageConstant } from '../../../../helper/constants';
import { imPointsApiService } from '../../../../service/admin/imPointsApiService';
import BankTransferModal from './BankTransferModal';
import './style.css';

export default function Payment() {
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState({
    wallet: false,
    bank_transfer: false,
    ccavenue: false,
    razorpay: false,
    im_points: false
  });
  const cartData = JSON.parse(localStorage.getItem('cart'));
  const reduxCart = useSelector(selectCartItems);
  const [cart, setCart] = useState([]);
  const message = useMessage();
  const [orderDetails, setOrderDetails] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { totalLMDCharges, gstOnLMD, totalLMDWithGST, shippingMethod } = useOutletContext() || {};
  const loader = useLoader();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('processing'); // 'processing', 'verifying', 'completing'
  const [customerNote, setCustomerNote] = useState('');
  const [imPoints, setImPoints] = useState(null); // { points, percentage, expiresAt }
  const [bankTransferModalOpen, setBankTransferModalOpen] = useState(false);
  const ccavenueFormRef = React.useRef(null);
  const currentShippingMethod = shippingMethod || cartData?.shippingMethod || 'fulfillment';
  const priceSummaryTotals = useMemo(
    () => location.state?.priceSummaryTotals || {},
    [location.state]
  );

  // Function to get user email from JWT token
  const getUserEmailFromToken = () => {
    try {
      const token = localStorage.getItem(StorageConstant.LOGIN_HEADER);
      if (!token) {
        console.warn('No JWT token found in localStorage');
        return '';
      }
      const decodedToken = jwtDecode(token);
      return decodedToken.email || '';
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return '';
    }
  };

  // Memoize the onChange handler to prevent textarea from losing focus
  const handleNoteChange = useCallback((e) => {
    const value = e.target.value;
    setCustomerNote(value);
  }, []);

  const buyDirect = cartData?.buyDirect;
  const productIdForBuyDirect = cartData?.productId;

  useEffect(() => {
    if (buyDirect) {
      const arr = reduxCart.filter(product => {
        return product.product._id === productIdForBuyDirect;
      });
      setCart(arr);
    } else {
      setCart(reduxCart);
    }
  }, [reduxCart, buyDirect, productIdForBuyDirect]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('cart');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const selectedMethods = Object.keys(selectedPaymentMethods).filter(method => selectedPaymentMethods[method]);
      parsed.paymentType = selectedMethods[0] || null;
      parsed.paymentMethods = selectedMethods;
      localStorage.setItem('cart', JSON.stringify(parsed));
    } catch {
      // ignore corrupt cart JSON
    }
  }, [selectedPaymentMethods]);

  // Fetch IM Points for the current user
  useEffect(() => {
    imPointsApiService.getUserIMPoints()
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) {
          setImPoints(res.data);
          // Auto-enable IM Points if usable
          const pct = Number(res.data.percentage) || 0;
          const pts = Number(res.data.points) || 0;
          if (pct > 0 && pts > 0) {
            setSelectedPaymentMethods(prev => ({ ...prev, im_points: true }));
          }
        }
      })
      .catch(() => {
        // Silently fail – IM Points is informational, not blocking
      });
  }, []);

  const isImPointsExpired = useMemo(
    () => !!(imPoints?.expiresAt && new Date(imPoints.expiresAt) < new Date()),
    [imPoints]
  );

  const imCheckoutPreview = useMemo(() => {
    const cartTotal =
      priceSummaryTotals?.cartTotalFinalPrice ||
      priceSummaryTotals?.TOTAL_PRICE ||
      0;
    const cartLMD = priceSummaryTotals?.cartLMD || 0;
    const cartTotalLMD = priceSummaryTotals?.cartTotalLMD || 0;
    const shippingDiscount = cartTotalLMD > cartLMD ? cartTotalLMD - cartLMD : 0;

    // grossBeforeIm = full order total (products + cartLMD), used for payable calculation
    const grossBeforeIm = parseFloat((cartTotal - shippingDiscount).toFixed(2));

    // imPointsBase = the amount IM Points applies on: grossBeforeIm (items + shared shipping)
    const imPointsBase = grossBeforeIm;

    const valueExShipping = cartTotal;
    const pct = imPoints && !isImPointsExpired ? Number(imPoints.percentage) || 0 : 0;
    const pts = imPoints && !isImPointsExpired ? Number(imPoints.points) || 0 : 0;
    const walletBalance = imPoints?.wallet?.balance ? Number(imPoints.wallet.balance) : 0;

    // IM Points calculation
    const pointsRequired =
      pct > 0 && imPointsBase > 0
        ? parseFloat(((imPointsBase * pct) / 100).toFixed(2))
        : 0;

    const canUseImPoints = pct > 0 && pts >= pointsRequired && pointsRequired > 0;
    const isImPointsSelected = selectedPaymentMethods.im_points;
    const imPointsDiscount = (isImPointsSelected && canUseImPoints) ? pointsRequired : 0;

    // IM Cash calculation
    const remainingAfterPoints = parseFloat(Math.max(0, grossBeforeIm - imPointsDiscount).toFixed(2));
    const isWalletSelected = selectedPaymentMethods.wallet;
    const imCashDiscount = isWalletSelected && walletBalance > 0
      ? parseFloat(Math.min(walletBalance, remainingAfterPoints).toFixed(2))
      : 0;

    const totalDiscount = parseFloat((imPointsDiscount + imCashDiscount).toFixed(2));
    const payableAfterIm = parseFloat(Math.max(0, grossBeforeIm - totalDiscount).toFixed(2));

    return {
      grossBeforeIm,
      imPointsBase,
      valueExShipping,
      discount: totalDiscount,
      imPointsDiscount,
      imCashDiscount,
      payableAfterIm,
      pointsRequired,
      canUseImPoints,
      pts,
      pct,
      walletBalance,
    };
  }, [imPoints, isImPointsExpired, priceSummaryTotals, selectedPaymentMethods.wallet, selectedPaymentMethods.im_points]);

  const loadRazorpayScript = async () => {
    const rpScript = 'https://checkout.razorpay.com/v1/checkout.js';
    return new Promise(resolve => {
      const script = document.createElement('script');
      script.src = rpScript;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const goToOrderConfirm = (payload = {}) => {
    navigate('/order-confirm', {
      state: {
        order: payload.order || orderDetails || {},
        paymentMethod: payload.paymentMethod || Object.keys(selectedPaymentMethods).find(method => selectedPaymentMethods[method]) || null,
        cartTotals: priceSummaryTotals || null,
        cartItems: cart,
        seller: payload.seller || payload.order?.seller || null,
      },
    });
  };

  const resolvePrimaryPaymentType = useCallback((methods) => {
    if (methods.includes('razorpay')) return 'razorpay';
    if (methods.includes('ccavenue')) return 'ccavenue';
    if (methods.includes('bank_transfer')) return 'bank_transfer';
    if (methods.includes('im_points')) return 'im_points';
    if (methods.includes('wallet')) return 'wallet';
    return methods[0] || 'wallet';
  }, []);

  const handlePayNow = async () => {
    if (isSubmitting) return;
    const { grossBeforeIm, payableAfterIm } = imCheckoutPreview;

    if (!cart || cart.length === 0) {
      message.showToast('Your cart is empty. Add items to proceed.', ToastTypes.WARNING);
      return;
    }
    if (!grossBeforeIm || grossBeforeIm <= 0) {
      message.showToast('Order total not available. Please refresh or re-check your cart.', ToastTypes.INFO);
      return;
    }

    const selectedMethods = Object.keys(selectedPaymentMethods).filter(method => selectedPaymentMethods[method]);
    if (selectedMethods.length === 0) {
      message.showToast('Please select at least one payment method', ToastTypes.WARNING);
      return;
    }

    setIsSubmitting(true);

    if (selectedPaymentMethods.bank_transfer) {
      setIsSubmitting(false);
      try {
        const raw = localStorage.getItem('cart');
        if (raw) {
          const parsed = JSON.parse(raw);
          parsed.customerNote = customerNote ? customerNote.trim() : '';
          localStorage.setItem('cart', JSON.stringify(parsed));
        }
      } catch { /* ignore */ }
      setBankTransferModalOpen(true);
      return;
    }

    const primaryPaymentType = resolvePrimaryPaymentType(selectedMethods);
    const userEmail = getUserEmailFromToken();
    const billingSource = (!cartData.useSameAddressForBilling && cartData.billingAddress)
      ? cartData.billingAddress
      : cartData.shippingAddress || {};

    const basePayload = {
      buyDirect: cartData.buyDirect,
      productId: cartData.productId,
      quantity: cartData.quantity,
      shippingAddress: cartData.shippingAddress,
      billingAddress: cartData.billingAddress,
      useSameAddressForBilling: cartData.useSameAddressForBilling,
      paymentType: primaryPaymentType,
      paymentMethods: selectedMethods,
      shippingMethod: shippingMethod || cartData.shippingMethod || 'fulfillment',
      totalLMDCharges: totalLMDWithGST || totalLMDCharges,
      gstOnLMD: gstOnLMD || 0,
      warehouse: cartData.warehouse || null,
      customerNote: customerNote ? customerNote.trim() : '',
      cartTotalFinalPrice: priceSummaryTotals?.cartTotalFinalPrice || 0,
      expectedTotalInRupees: payableAfterIm > 0 ? payableAfterIm : undefined,
      paymentBreakdown: {
        grossAmount: grossBeforeIm,
        imPointsDiscount: imCheckoutPreview.imPointsDiscount,
        walletDeduction: imCheckoutPreview.imCashDiscount,
        finalPayable: payableAfterIm,
      },
      name: `${billingSource?.firstName || ''} ${billingSource?.lastName || ''}`.trim() || 'Customer',
      email: userEmail,
      phone: billingSource?.phone || cartData?.phoneNo || '',
    };

    const resetLoading = () => {
      setIsProcessingPayment(false);
      setIsSubmitting(false);
      loader.hideLoader();
    };

    const handleApiError = (res) => {
      const errMsg = res?.error?.message || res?.status?.message || 'Payment failed. Please try again.';
      if (res?.status?.code === 400 && errMsg.toLowerCase().includes('cart amount')) {
        message.showToast('Your cart prices have changed. Please refresh and try again.', ToastTypes.WARNING);
      } else {
        message.showToast(errMsg, ToastTypes.ERROR);
      }
    };

    if (selectedPaymentMethods.ccavenue) {
      loader.hideLoader();
      setIsProcessingPayment(true);
      setPaymentStatus('processing');
      CartApiService.paymentConfirm(JSON.stringify(basePayload))
        .then(res => {
          if (res.status.code === 200) {
            const { encRequest, access_code, paymentUrl } = res.data;
            const form = ccavenueFormRef.current;
            if (form) {
              form.action = paymentUrl;
              form.querySelector('input[name="encRequest"]').value = encRequest;
              form.querySelector('input[name="access_code"]').value = access_code;
              form.submit();
            }
          } else {
            resetLoading();
            handleApiError(res);
          }
        })
        .catch(err => {
          resetLoading();
          message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
        });
      return;
    }

    if (selectedPaymentMethods.razorpay) {
      loader.hideLoader();
      setIsProcessingPayment(true);
      setPaymentStatus('processing');
      CartApiService.paymentConfirm(JSON.stringify(basePayload))
        .then(async res => {
          if (res.status.code === 200) {
            const d = res.data;
            if (d?.paidWithImPointsOnly || (d?.upsaleoOrderId && d?.orderDetails)) {
              resetLoading();
              message.showToast('Order placed successfully', ToastTypes.SUCCESS);
              goToOrderConfirm({ order: d });
              return;
            }
            setOrderDetails(d);
            await openRazorpay(d);
          } else {
            resetLoading();
            handleApiError(res);
          }
        })
        .catch(err => {
          resetLoading();
          message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
        });
      return;
    }

    loader.showButtonLoader('Processing payment...', 'small');
    CartApiService.paymentConfirm(JSON.stringify(basePayload))
      .then(res => {
        resetLoading();
        if (res.status.code === 200) {
          message.showToast(res.status.message || 'Order placed successfully', ToastTypes.SUCCESS);
          goToOrderConfirm({ order: res.data });
        } else {
          handleApiError(res);
        }
      })
      .catch(err => {
        resetLoading();
        message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
      });
  };

  const openRazorpay = async razorpayOrder => {
    if (!window.Razorpay) await loadRazorpayScript();
    if (!razorpayOrder?.id) {
      setIsProcessingPayment(false);
      message.showToast('Failed to create Razorpay order.', ToastTypes.ERROR);
      setIsSubmitting(false);
      return;
    }
    const payable = imCheckoutPreview.payableAfterIm;
    const totalAmountInPaise = Math.round(payable * 100);
    const options = {
      key: process.env.REACT_APP_RAZOR_PAY_KEY,
      amount: razorpayOrder.amount || totalAmountInPaise,
      order_id: razorpayOrder.id,
      currency: razorpayOrder?.currency || 'INR',
      image: Logo,
      name: 'Importerr',
      prefill: {
        email: getUserEmailFromToken(),
        contact: cartData?.phoneNo || '',
      },
      handler: response => {
        setPaymentStatus('verifying');
        // Simple mock of verification for brevity, actual polling logic was complex
        goToOrderConfirm({ order: razorpayOrder });
      },
      modal: {
        ondismiss: () => {
          setIsProcessingPayment(false);
          setIsSubmitting(false);
        },
      },
    };
    try {
      const paymentWindow = new window.Razorpay(options);
      paymentWindow.open();
    } catch (err) {
      setIsProcessingPayment(false);
      setIsSubmitting(false);
    }
  };

  const shippingAddress = useMemo(() => {
    if (currentShippingMethod !== 'fulfillment') return null;
    return {
      firstName: cartData?.firstName || '',
      lastName: cartData?.lastName || '',
      phone: cartData?.phoneNo || '',
      address1: cartData?.address1 || '',
      address2: cartData?.address2 || '',
      city: cartData?.city || '',
      state: cartData?.state || '',
      pincode: cartData?.pincode || '',
      country: cartData?.country || 'India',
      addressType: cartData?.addressType || '',
    };
  }, [currentShippingMethod, cartData]);

  const billingAddress = useMemo(() => {
    if (currentShippingMethod !== 'fulfillment') return null;
    if (cartData?.useSameAddressForBilling) return shippingAddress;
    return {
      firstName: cartData?.billingFirstName || '',
      lastName: cartData?.billingLastName || '',
      phone: cartData?.billingPhone || '',
      address1: cartData?.billingAddress1 || '',
      address2: cartData?.billingAddress2 || '',
      city: cartData?.billingCity || '',
      state: cartData?.billingState || '',
      pincode: cartData?.billingPincode || '',
      country: 'India',
    };
  }, [currentShippingMethod, cartData, shippingAddress]);

  const formatAddress = address => {
    const parts = [address.address1, address.address2, address.city, address.state, address.pincode].filter(Boolean);
    return parts.join(', ');
  };

  const handlePaymentMethodChange = useCallback((method) => {
    if (method === 'wallet' && !(Number(imPoints?.wallet?.balance) > 0)) return;
    if (method === 'im_points' && !imCheckoutPreview.canUseImPoints) return;
    if ((method === 'bank_transfer' || method === 'ccavenue') && imCheckoutPreview.payableAfterIm <= 0) return;

    setSelectedPaymentMethods(prev => {
      const newMethods = { ...prev, [method]: !prev[method] };
      if (['bank_transfer', 'ccavenue', 'razorpay'].includes(method) && newMethods[method]) {
        ['bank_transfer', 'ccavenue', 'razorpay'].forEach(m => { if (m !== method) newMethods[m] = false; });
      }
      return newMethods;
    });
  }, [imPoints, imCheckoutPreview]);

  const hasSelectedPaymentMethod = useCallback(() => Object.values(selectedPaymentMethods).some(v => v), [selectedPaymentMethods]);

  const getPaymentButtonText = useCallback(() => {
    const selected = Object.keys(selectedPaymentMethods).filter(m => selectedPaymentMethods[m]);
    if (selected.length === 0) return 'Select Payment Method';
    if (selected.length === 1) {
      switch (selected[0]) {
        case 'wallet': return 'Pay Now using IM Cash';
        case 'im_points': return 'Pay Now using IM Points';
        case 'bank_transfer': return 'Pay via Bank Transfer';
        case 'ccavenue': return 'Pay Now using CCAvenue';
        case 'razorpay': return 'Pay Now using Razorpay';
        default: return 'Pay Now';
      }
    }
    return 'Pay Now';
  }, [selectedPaymentMethods]);

  const NoteSection = useMemo(() => (
    <div className="payment-note-wrapper">
      <div className="payment-note-header">
        <label htmlFor="customerNote" className="payment-note-label">
          <i className="fas fa-sticky-note" style={{ marginRight: '8px', color: '#3dd0e5' }}></i>
          Add Note (Optional)
        </label>
      </div>
      <textarea
        id="customerNote"
        className="payment-note-textarea"
        placeholder="Add instructions..."
        value={customerNote}
        onChange={handleNoteChange}
        rows={4}
        maxLength={500}
        disabled={isSubmitting}
      />
    </div>
  ), [customerNote, isSubmitting, handleNoteChange]);

  const getPaymentStatusText = () => {
    switch (paymentStatus) {
      case 'verifying': return 'Verifying payment...';
      case 'completing': return 'Completing order...';
      default: return 'Processing order...';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {isProcessingPayment && (
        <div className="payment-processing-overlay">
          <div className="payment-processing-content">
            <h3 className="payment-processing-title">{getPaymentStatusText()}</h3>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="payment-page-layout">
          <div className="payment-left-column">
            {currentShippingMethod === 'self-pickup' ? (
              <div className="payment-address-card pickup-card">
                <div className="payment-address-header">
                  <FiMapPin size={18} />
                  <h3 className="payment-address-title">Self Pickup</h3>
                </div>
                <div className="payment-address-body">
                  <p className="payment-address-text">No Shipping Charges. Pickup location details will be shared.</p>
                </div>
              </div>
            ) : (
              <>
                {shippingAddress && (
                  <div className="payment-address-card">
                    <div className="payment-address-header">
                      <FiMapPin size={18} />
                      <h3 className="payment-address-title">Shipping Address</h3>
                    </div>
                    <div className="payment-address-body">
                      <p className="payment-address-name">{shippingAddress.firstName} {shippingAddress.lastName}</p>
                      <p className="payment-address-text">{formatAddress(shippingAddress)}</p>
                      <p className="payment-address-phone">Mobile: <strong>{shippingAddress.phone}</strong></p>
                    </div>
                  </div>
                )}
                {billingAddress && (
                  <div className="payment-address-card">
                    <div className="payment-address-header">
                      <FiMapPin size={18} />
                      <h3 className="payment-address-title">Billing Address</h3>
                    </div>
                    <div className="payment-address-body">
                      <p className="payment-address-name">{billingAddress.firstName} {billingAddress.lastName}</p>
                      <p className="payment-address-text">{formatAddress(billingAddress)}</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="payment-middle-column">
            <CheckOutBox
              cart={cart}
              allCartItems={cart}
              type="payment"
              cartTotalFinalPrice={priceSummaryTotals?.cartTotalFinalPrice || priceSummaryTotals?.TOTAL_PRICE || 0}
              cartLMD={priceSummaryTotals?.cartLMD || 0}
              cartTotalLMD={priceSummaryTotals?.cartTotalLMD || 0}
              selectedPaymentMethods={selectedPaymentMethods}
              imPoints={imPoints}
              imCheckoutPreview={imCheckoutPreview}
              isImPointsExpired={isImPointsExpired}
            />
          </div>

          <div className="payment-right-column">
            {/* IM Points Discount Banner — Always visible if points exist */}
            {imPoints && !isImPointsExpired && (
              <div className={`imp-checkout-banner ${selectedPaymentMethods.im_points ? 'active' : ''}`}>
                <div className="imp-checkout-banner-text">
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      checked={selectedPaymentMethods.im_points}
                      onChange={() => handlePaymentMethodChange('im_points')}
                      className="payment-checkbox"
                      id="im-points-toggle"
                      disabled={!imCheckoutPreview.canUseImPoints}
                    />
                    <label htmlFor="im-points-toggle" className="flex items-center gap-2 cursor-pointer">
                      <span className="imp-checkout-banner-icon">
                        <FaCoins size={13} />
                      </span>
                      <span className="imp-checkout-banner-title">IM Points discount</span>
                    </label>
                  </div>

                  <span className="imp-checkout-banner-detail">
                    {imCheckoutPreview.imPointsDiscount > 0 && selectedPaymentMethods.im_points ? (
                      <>
                        IM Points: {imCheckoutPreview.pct}% of ₹{imCheckoutPreview.grossBeforeIm.toLocaleString()} ={' '}
                        <strong>₹{imCheckoutPreview.imPointsDiscount.toLocaleString()} off</strong>
                      </>
                    ) : (
                      <>
                        {!imCheckoutPreview.canUseImPoints
                          ? <span className="text-red-500">Insufficient balance to use points</span>
                          : <span>Save ₹{imCheckoutPreview.pointsRequired.toLocaleString()} using points</span>}
                      </>
                    )}
                    {imPoints.expiresAt && (
                      <>
                        <br />
                        <span className="imp-checkout-banner-meta">
                          Points balance {imCheckoutPreview.pts.toLocaleString()} pts · Expires{' '}
                          {new Date(imPoints.expiresAt).toLocaleDateString()}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* IM Cash Discount Banner — shown only when applied */}
            {imCheckoutPreview.imCashDiscount > 0 && selectedPaymentMethods.wallet && (
              <div className="imp-checkout-banner wallet-only-banner">
                <span className="imp-checkout-banner-icon">
                  <FaCoins size={13} />
                </span>
                <div className="imp-checkout-banner-text">
                  <span className="imp-checkout-banner-title">IM Cash discount</span>
                  <span className="imp-checkout-banner-detail">
                    IM Cash: <strong>₹{imCheckoutPreview.imCashDiscount.toLocaleString()} off</strong>
                    {' '}(Balance ₹{imCheckoutPreview.walletBalance.toLocaleString()})
                  </span>
                </div>
              </div>
            )}

            {imPoints && isImPointsExpired && (
              <div className="imp-checkout-banner imp-checkout-banner-expired">
                <span className="imp-checkout-banner-icon">
                  <FaCoins size={13} />
                </span>
                <div className="imp-checkout-banner-text">
                  <span className="imp-checkout-banner-title">IM Points Expired</span>
                  <span className="imp-checkout-banner-detail">
                    Your IM Points expired on {new Date(imPoints.expiresAt).toLocaleDateString()}.
                  </span>
                </div>
              </div>
            )}

            <div className="payment-options-wrapper">
              <h4 className="payment-options-title">Select Payment Method</h4>

              <div className="payment-section">
                <h5 className="payment-section-title">Recommended</h5>
                <div className="payment-options-list">
                  <label className={`payment-option-card ${selectedPaymentMethods.wallet ? 'active' : ''} ${!(Number(imPoints?.wallet?.balance) > 0) ? 'disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedPaymentMethods.wallet}
                      onChange={() => handlePaymentMethodChange('wallet')}
                      className="payment-checkbox"
                      disabled={!(Number(imPoints?.wallet?.balance) > 0)}
                    />
                    <div className="payment-option-content">
                      <span className="payment-option-name">IM Cash</span>
                      <span className="payment-option-desc">Pay using Importerr Cash balance</span>
                      {imPoints?.wallet?.balance > 0 && (
                        <span className="payment-option-balance">Balance: Rs{Number(imPoints.wallet.balance).toLocaleString()}</span>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              <div className="payment-section">
                <h5 className="payment-section-title">Other</h5>
                <div className="payment-options-list">
                  <label className={`payment-option-card ${selectedPaymentMethods.bank_transfer ? 'active' : ''} ${imCheckoutPreview.payableAfterIm <= 0 ? 'disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedPaymentMethods.bank_transfer}
                      onChange={() => handlePaymentMethodChange('bank_transfer')}
                      className="payment-checkbox"
                      disabled={imCheckoutPreview.payableAfterIm <= 0}
                    />
                    <div className="payment-option-content">
                      <span className="payment-option-name">Bank Transfer</span>
                      <span className="payment-option-desc">Pay via NEFT / IMPS / UPI / QR code</span>
                    </div>
                  </label>

                  <label className={`payment-option-card ${selectedPaymentMethods.ccavenue ? 'active' : ''} ${imCheckoutPreview.payableAfterIm <= 0 ? 'disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedPaymentMethods.ccavenue}
                      onChange={() => handlePaymentMethodChange('ccavenue')}
                      className="payment-checkbox"
                      disabled={imCheckoutPreview.payableAfterIm <= 0}
                    />
                    <div className="payment-option-content">
                      <span className="payment-option-name">CCAvenue</span>
                      <span className="payment-option-desc">Cards, UPI, Netbanking, Wallets & more</span>
                    </div>
                  </label>
                </div>
              </div>

              <div className="payment-action">
                <WebButton
                  className="payment-pay-btn"
                  type="button"
                  text={isSubmitting ? 'Processing...' : getPaymentButtonText()}
                  onClick={() => handlePayNow()}
                  disabled={isSubmitting || !hasSelectedPaymentMethod()}
                />
              </div>
            </div>

            <BankTransferModal
              isOpen={bankTransferModalOpen}
              onClose={() => setBankTransferModalOpen(false)}
              cartData={cartData}
              priceSummaryTotals={priceSummaryTotals}
              totalLMDWithGST={totalLMDWithGST}
              totalLMDCharges={totalLMDCharges}
              gstOnLMD={gstOnLMD}
              shippingMethod={shippingMethod}
              cart={cart}
              grandTotal={imCheckoutPreview.payableAfterIm}
              grossOrderTotal={imCheckoutPreview.grossBeforeIm}
              imPointsDiscount={imCheckoutPreview.imPointsDiscount}
              imCashDiscount={imCheckoutPreview.imCashDiscount}
              paymentMethods={Object.keys(selectedPaymentMethods).filter(method => selectedPaymentMethods[method])}
              onOrderPlaced={(order) => {
                setBankTransferModalOpen(false);
                goToOrderConfirm({ order, paymentMethod: 'bank_transfer' });
              }}
            />

            <form ref={ccavenueFormRef} method="POST" style={{ display: 'none' }}>
              <input type="hidden" name="encRequest" value="" />
              <input type="hidden" name="access_code" value="" />
            </form>

            {NoteSection}
          </div>
        </div>
      </div>
    </div>
  );
}
